import { useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableCard } from './SortableCard';
import type { BoardSettings, Card, Column } from '../types/board';

interface ColumnComponentProps {
  column: Column;
  onAddCard: (content: string) => Promise<void>;
  settings: BoardSettings;
  isAdmin?: boolean;
  userId?: string;
  revealAllCards?: boolean;
  onEditCard?: (card: Card) => void;
  onDeleteCard?: (cardId: string) => Promise<void>;
  onVoteCard?: (cardId: string) => Promise<void>;
  onRemoveCardVote?: (cardId: string) => Promise<void>;
  viewerVoteTotal?: number;
  onEditColumn?: () => void;
  onDeleteColumn?: () => void;
}

const DEFAULT_COLUMN_COLOR = '#4F7EE8';

function normalizeColumnColor(color: string | undefined): string {
  if (color && /^#[0-9A-Fa-f]{6}$/.test(color)) {
    return color;
  }
  return DEFAULT_COLUMN_COLOR;
}

function hexToRgba(hexColor: string, alpha: number): string {
  const red = parseInt(hexColor.slice(1, 3), 16);
  const green = parseInt(hexColor.slice(3, 5), 16);
  const blue = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function ColumnComponent({
  column,
  onAddCard,
  settings,
  isAdmin = false,
  userId,
  revealAllCards = false,
  onEditCard,
  onDeleteCard,
  onVoteCard,
  onRemoveCardVote,
  viewerVoteTotal = 0,
  onEditColumn,
  onDeleteColumn,
}: ColumnComponentProps): JSX.Element {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingCard, setIsAddingCard] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: column.id,
    data: { column, type: 'column' },
    disabled: { draggable: !isAdmin, droppable: false },
  });

  const accentColor = normalizeColumnColor(column.color);
  const columnStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    borderColor: accentColor,
  };
  const headerStyle: CSSProperties = {
    backgroundColor: hexToRgba(accentColor, 0.16),
    color: accentColor,
  };
  const addButtonStyle: CSSProperties = {
    backgroundColor: accentColor,
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmed = content.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onAddCard(trimmed);
      setContent('');
      setIsAddingCard(false);
    } catch (error) {
      console.error('Failed to add card:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    setContent('');
    setIsAddingCard(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={columnStyle}
      data-testid="board-column"
      data-column-id={column.id}
      data-column-name={column.name}
      className={`flex min-w-[20rem] flex-[1_1_20rem] flex-col overflow-hidden rounded-lg border-2 bg-white shadow-sm dark:bg-gray-900 ${
        isOver ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-950' : ''
      } ${isDragging ? 'z-50 shadow-xl' : ''}`}
    >
      {/* Column header */}
      <div
        className="px-5 py-4"
        style={headerStyle}
      >
        <div className="flex flex-col gap-2">
          <div
            className={`flex items-center gap-3 ${
              isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
            }`}
            {...(isAdmin ? { ...attributes, ...listeners } : {})}
          >
            <h3 className="min-w-0 flex-1 break-words text-sm font-semibold">
              {column.name}
            </h3>
            {isAdmin && (
              <svg
                className="h-4 w-4 flex-shrink-0 opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8h16M4 16h16"
                />
              </svg>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold">
            <span>{column.cards.length} cards</span>
            {isAdmin && onEditColumn && (
              <button
                type="button"
                onClick={onEditColumn}
                onPointerDown={(event) => event.stopPropagation()}
                className="rounded p-1 opacity-75 transition hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"
                title="Edit column"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            {isAdmin && onDeleteColumn && (
              <button
                type="button"
                onClick={onDeleteColumn}
                onPointerDown={(event) => event.stopPropagation()}
                className="rounded p-1 text-red-600 opacity-75 transition hover:bg-red-500/10 hover:opacity-100 dark:text-red-400"
                title="Delete column"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <SortableContext
          items={column.cards.map((card) => card.id)}
          strategy={verticalListSortingStrategy}
        >
          {column.cards.map((card) => (
            <SortableCard
              key={card.id}
              card={card}
              settings={settings}
              isAdmin={isAdmin}
              userId={userId}
              revealAllCards={revealAllCards}
              accentColor={accentColor}
              onEdit={onEditCard ? () => onEditCard(card) : undefined}
              onDelete={onDeleteCard ? () => onDeleteCard(card.id) : undefined}
              onVote={onVoteCard ? () => onVoteCard(card.id) : undefined}
              onRemoveVote={onRemoveCardVote ? () => onRemoveCardVote(card.id) : undefined}
              viewerVoteTotal={viewerVoteTotal}
            />
          ))}
        </SortableContext>

        {settings.enableCardGroups && (
          <button
            type="button"
            disabled
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
            title="Card group creation is not available in this view yet"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V9a2 2 0 00-2-2h-3.5l-2-2H6a2 2 0 00-2 2v6m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0H4"
              />
            </svg>
            Create Group
          </button>
        )}

        {isAddingCard ? (
          <div className="space-y-2">
            <textarea
              data-testid="card-content-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter card content..."
              disabled={isSubmitting}
              rows={3}
              autoFocus
              className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={!content.trim() || isSubmitting}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Card'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsAddingCard(true)}
            className="w-full rounded-md px-3 py-2 text-sm font-semibold text-white transition hover:brightness-110"
            style={addButtonStyle}
          >
            + Add Card
          </button>
        )}
      </div>
    </div>
  );
}
